import { ApiClient } from '../ApiClient';

export class LPBondsStatsApi {
  /**
   * Constructs a new LPBondsStatsApi.
   * @alias module:api/LPBondsStatsApi
   * @class
   * @param {module:ApiClient} [apiClient] Optional API client implementation to use,
   * default to {@link module:ApiClient#lpBondsApiInstance} if unspecified.
   */
  constructor(apiClient) {
    this.apiClient = apiClient || ApiClient.lpBondsApiInstance;
  }

  /**
   * Get collection price statistic
   */
  async getCollectionPriceStats() {
    return await this.apiClient.callApi('/stats/collection', 'get', {}, {}, null);
  }

  /**
   * Get collection stats by address
   * @param {string} address Collection address
   */
  async getCollectionStats(address) {
    return await this.apiClient.callApi(
      '/stats/collection/{address}',
      'get',
      { address },
      {},
      null,
    );
  }

  /**
   * Get activity statistic
   * @param {string} collection Single collection address (legacy parameter)
   * @param {string} collections Comma-separated collection addresses for multiple collections
   * @param {string} listing Listing tx type - 'true' | 'false'
   * @param {string} mint Mint tx type - 'true' | 'false'
   * @param {Number} sale Sale tx type - 'true' | 'false'
   * @param {Number} offer Offer tx type - 'true' | 'false'
   * @param {Number} skip Numer of items to skip for the result set
   * @param {Number} limit Number of items to retrieve
   */
  async getActivityStats({
    collection,
    collections,
    listing,
    mint,
    sale,
    offer,
    bid,
    transfer,
    burn,
    skip,
    limit,
    date,
  }) {
    return await this.apiClient.callApi(
      '/stats/activities',
      'get',
      {},
      {
        collection,
        collections,
        listing,
        mint,
        sale,
        offer,
        bid,
        transfer,
        burn,
        skip,
        limit,
        date,
      },
      null,
    );
  }

  /**
   * Get top collection statistic
   * @param {string} market Market type - 'all' | 'ethereum' | 'energi' | 'bsc'
   * @param {Number} period Period type - 0: 24h | 1: 7d | 2: 30d | 3: all
   * @param {string} direction Order direction - 'asc' | 'desc'
   * @param {Number} skip Number of items to skip for the result set
   * @param {Number} limit Number of items to retrieve
   */
  async getTopCollectionsStats({ market, period, direction, skip, limit }) {
    return await this.apiClient.callApi(
      '/stats/topCollections',
      'get',
      {},
      {
        market,
        period,
        direction,
        skip,
        limit,
      },
      null,
    );
  }

  /**
   * Get user activity statistic
   * @param {string} address User address
   * @param {string} collection Single collection address (legacy parameter)
   * @param {string} collections Comma-separated collection addresses for multiple collections
   * @param {string} listing Listing tx type - 'true' | 'false'
   * @param {string} mint Mint tx type - 'true' | 'false'
   * @param {Number} sale Sale tx type - 'true' | 'false'
   * @param {Number} offer Offer tx type - 'true' | 'false'
   * @param {Number} skip Number of items to skip for the result set
   * @param {Number} limit Number of items to retrieve
   */
  async getUserActivityStats({
    address,
    collection,
    collections,
    listing,
    mint,
    sale,
    offer,
    bid,
    transfer,
    burn,
    skip,
    limit,
    date,
  }) {
    return await this.apiClient.callApi(
      '/user/{address}/activities',
      'get',
      { address },
      {
        collection,
        collections,
        listing,
        mint,
        sale,
        offer,
        bid,
        transfer,
        burn,
        skip,
        limit,
        date,
      },
      null,
    );
  }

  /**
   * Get all LP Bonds for the current chain with stats and metadata
   * This unified endpoint returns all bond data in one call
   * Now includes grouped data for landing page display
   * @returns {Promise<{totalItems: number, totalGroups: number, chain: string, data: Array, grouped: Array}>}
   */
  async getLPBonds() {
    return await this.apiClient.callApi('/stats/lpbonds', 'get', {}, {}, null);
  }

  /**
   * Get lightweight LP Bonds data for filter dropdowns
   * Returns only essential fields needed for collection filter UI
   * @returns {Promise<{totalItems: number, chain: string, data: Array}>}
   */
  async getLPBondsFilter() {
    return await this.apiClient.callApi('/stats/lpbonds/filter', 'get', {}, {}, null);
  }

  /**
   * Get global stats history (TVL and bond counts over time)
   * @param {Number} days Number of days of history (0 = all-time, default 0)
   * @param {string} chain Chain filter ('all', 'ethereum', 'apechain', 'arbitrum', 'monad')
   */
  async getGlobalStatsHistory(days = 0, chain = 'all') {
    return await this.apiClient.callApi('/stats/global/history', 'get', {}, { days, chain }, null);
  }

  /**
   * Get current global stats (latest TVL and bond counts)
   * @param {string} chain Chain filter ('all', 'ethereum', 'apechain', 'arbitrum', 'monad')
   */
  async getCurrentGlobalStats(chain = 'all') {
    return await this.apiClient.callApi('/stats/global/current', 'get', {}, { chain }, null);
  }

  /**
   * Get bonds configuration (single source of truth)
   * Returns APY by level, chain IDs, and all bond pair configurations
   * This data rarely changes - cache aggressively on the frontend
   * @returns {Promise<{apyByLevel: Object, chains: Object, pairs: Object}>}
   */
  async getBondsConfig() {
    return await this.apiClient.callApi('/stats/lpbonds/config', 'get', {}, {}, null);
  }
}
